/**
 * Per-user view preferences — `GET/PUT /v2/user/me/view-preferences/{view_key}/`
 * (backend OR-1385).
 *
 * The backend stores an **opaque JSON object** owned entirely by the
 * frontend: columns have no server-side representation, so validating the
 * blob would couple every layout change to a backend deploy. That means
 * the shape below is ours to define — and ours to keep backwards
 * compatible (see `view-preference-config.ts` for the versioning).
 *
 * Contract notes that matter to callers (`orion-backend/docs/view-preferences.md`):
 *  - GET **never 404s** — an unsaved view comes back as `{ config: {} }`.
 *  - PUT is a **full replacement**, no server-side merge. Send the whole
 *    config every time.
 *  - Writes are last-write-wins (get-then-write, not `ON CONFLICT`), which
 *    is fine for preferences.
 *  - `config` is capped at 32 KB serialized; a triage layout is orders of
 *    magnitude under that.
 *  - `view_key` is validated against `^[a-z0-9_.]{1,64}$` **only** — there
 *    is no registry of views, so a typo silently reads and writes its own
 *    empty slot. Keys must therefore be constants, never built at runtime;
 *    see `VIEW_KEYS`.
 */
import { getJsonReal, sendJsonReal } from "../client";

/** The frontend-owned blob. Opaque to the backend. */
export type ViewPreferenceConfig = Record<string, unknown>;

interface ViewPreferenceWire {
    view_key: string;
    config: ViewPreferenceConfig | null;
}

/**
 * Every view key the app persists, by the backend's `<section>.<view>`
 * convention. Frozen constants on purpose: a mistyped key does not fail,
 * it just silently reads an empty slot forever.
 */
export const VIEW_KEYS = {
    triageSubmissions: "submission_triage.submission",
    triageFacilities: "submission_triage.facility",
    /** The Policies list's last layout (OR-1664). */
    policies: "policies.list",
} as const;

export type ViewKey = (typeof VIEW_KEYS)[keyof typeof VIEW_KEYS];

/** The saved config for a view. `{}` when the user has saved nothing. */
export async function getViewPreference(viewKey: ViewKey): Promise<ViewPreferenceConfig> {
    const wire = await getJsonReal<ViewPreferenceWire>(`/v2/user/me/view-preferences/${viewKey}/`);
    return wire.config ?? {};
}

/** Replace the saved config for a view. Pass `{}` to reset to defaults. */
export async function putViewPreference(
    viewKey: ViewKey,
    config: ViewPreferenceConfig,
): Promise<ViewPreferenceConfig> {
    const wire = await sendJsonReal<ViewPreferenceWire>(
        `/v2/user/me/view-preferences/${viewKey}/`,
        "PUT",
        { config },
    );
    return wire.config ?? {};
}
